import React, { useState } from 'react';
import { Button, Tooltip, Modal } from 'antd';
import { useNavigate } from 'react-router-dom';
import { sendMessage } from 'api/inbox';
import { getUserId } from 'utils/localstore';
import { unitAndSortServerMessages } from 'utils/inbox';
import { TServerMessage } from 'types/inbox';
import { Prospect } from 'types/common';
import { PATH } from 'constants/path';
import useGetProviderAppointmentList from 'hooks/useGetProviderAppointmentList';
import dayjs from 'utils/dayjs';
import { getTimeStr } from 'utils/calendar';
import s from './s.module.less';
import { EScheduleStatusValue } from 'types/calendar';

type Props = {
    targetClient?: Prospect;
    patientId?: number;
    selectConversationId?: string;
    conversationMessageMap: React.MutableRefObject<Record<string, TServerMessage[] | undefined>>;
    setMessageList: (messages: TServerMessage[]) => void;
    scrollToLatestMessage: (timeout?: number, smooth?: boolean) => void;
    resetAutoFetchInterval: () => void;
    refetchConversation: (size: number, clientId?: string) => void;
    refetchAllUnreadCount: () => void;
    fetchConversationSize: React.MutableRefObject<number>;
    urlClientId: React.MutableRefObject<string | undefined>;
    run: () => void;
};

const AppointmentReminderButton = (props: Props) => {
    const {
        targetClient,
        patientId,
        selectConversationId,
        conversationMessageMap,
        setMessageList,
        scrollToLatestMessage,
        resetAutoFetchInterval,
        refetchConversation,
        refetchAllUnreadCount,
        fetchConversationSize,
        urlClientId,
        run,
    } = props;

    const [sendingAppointmentReminder, setSendingAppointmentReminder] = useState<boolean>(false);
    const [showNoMeetingLinkModal, setShowNoMeetingLinkModal] = useState<boolean>(false);
    const navigate = useNavigate();
    const providerId = getUserId();

    // Check for upcoming appointments
    const [upcomingAppointments, , fetchAppointments] = useGetProviderAppointmentList({
        current: 1,
        size: 10,
        type: 'Upcoming',
        clientId: targetClient?.id || patientId,
        autoFetch: true,
    });

    // Function to format appointment details for reminder message
    const formatAppointmentDetails = () => {
        if (!upcomingAppointments || upcomingAppointments.length === 0) {
            return {
                date: '[Date]',
                time: '[Time]',
                meetingLink: '[Appointment meeting link]',
            };
        }

        // Get the first upcoming appointment
        const appointment = upcomingAppointments[0];

        // Format date and time
        const startAt = dayjs(appointment.startAt);
        const formattedDate = startAt.format('dddd, MMMM D, YYYY');
        const formattedTime = startAt.format('h:mm A');

        // Extract meeting link based on contact type
        let meetingLink = '';

        if (appointment.contactType === 'video' && appointment.externalVideoLink) {
            meetingLink = appointment.externalVideoLink;
        } else if (appointment.contactType === 'in-person' && appointment.location) {
            meetingLink = `Location: ${appointment.location}`;
        } else if (appointment.contactType === 'telephone' && appointment.telephone) {
            meetingLink = `Phone: ${appointment.telephone}`;
        }

        return {
            date: formattedDate,
            time: formattedTime,
            meetingLink,
        };
    };

    const handleSendAppointmentReminder = async () => {
        if (!targetClient?.id) {
            run();
        }

        await fetchAppointments(1, 10, 'Upcoming', targetClient?.id || patientId);

        const appointmentDetails = formatAppointmentDetails();
        console.log('appointmentDetails.meetingLink: ', appointmentDetails.meetingLink);

        if (!appointmentDetails.meetingLink) {
            setShowNoMeetingLinkModal(true);
            return;
        }

        const reminderMessage = `Hi ${targetClient?.firstName || targetClient?.lastName || 'Patient'},
This is a reminder about your upcoming appointment with us on ${appointmentDetails.date} at ${appointmentDetails.time}.
Please make sure to attend at the scheduled time. If you need to reschedule or have any questions, feel free to contact us.
Meeting link: ${appointmentDetails.meetingLink}
Looking forward to seeing you soon!`;

        setSendingAppointmentReminder(true);
        const res = await sendMessage({
            providerId: providerId!,
            clientId: targetClient?.id! || patientId!,
            message: reminderMessage.replace(/<[^>]+>/g, ''),
        });
        setSendingAppointmentReminder(false);

        if (!res.error) {
            // message.success('Appointment reminder sent successfully!');
            resetAutoFetchInterval();
            setTimeout(() => {
                refetchConversation(fetchConversationSize.current, urlClientId.current);
                refetchAllUnreadCount();
            }, 2000);

            //add local message
            const cacheMessageList = conversationMessageMap.current[selectConversationId!] || [];
            const messageData: TServerMessage = res.data?.data;

            const _message = unitAndSortServerMessages([...(cacheMessageList || []), messageData]);
            conversationMessageMap.current[selectConversationId!] = _message || [];
            setMessageList(_message || []);
            scrollToLatestMessage(100, true);
            //end add local message
        }
    };

    const handleGoToAppointmentManagement = () => {
        setShowNoMeetingLinkModal(false);

        if (upcomingAppointments && upcomingAppointments.length > 0) {
            const appointment = upcomingAppointments[0];
            if (appointment?.id && appointment?.startAt) {
                const dateParam = getTimeStr(dayjs(appointment.startAt), 'MM-DD-YYYY');
                navigate(`${PATH.DASHBOARD}/${PATH.CALENDAR}?aid=${appointment.id}&date=${dateParam}`);
                return;
            }
        }

        navigate(`${PATH.DASHBOARD}/${PATH.CALENDAR}`);
    };

    const handleCloseModal = () => {
        setShowNoMeetingLinkModal(false);
    };

    // Only show when provider has upcoming appointment with patient
    if (
        !targetClient ||
        !upcomingAppointments ||
        upcomingAppointments.length === 0 ||
        !upcomingAppointments.some((item) => item.appointmentStatus === EScheduleStatusValue.SCHEDULED)
    ) {
        return null;
    }

    const appointmentDetails = formatAppointmentDetails();

    return (
        <div className={s.appointmentReminderWrap}>
            <Button
                className={s.appointmentReminderBtn}
                loading={sendingAppointmentReminder}
                onClick={handleSendAppointmentReminder}
            >
                <span>Send appointment reminder</span>
                <Tooltip
                    overlayClassName={s.tipCard}
                    color="#FFFFFF"
                    overlayInnerStyle={{ padding: '16px', borderRadius: '24px' }}
                    title={
                        <div className={s.tooltipContent}>
                            <div className={s.tooltipTitle}>📩 Appointment reminder message preview</div>
                            <div className={s.tooltipMessage}>
                                <p>Hi [Patient&apos;s Name],</p>
                                <p>
                                    This is a reminder about your upcoming appointment with us on{' '}
                                    {appointmentDetails.date} at {appointmentDetails.time}.
                                </p>
                                <p>
                                    Please make sure to attend at the scheduled time. If you need to reschedule or have
                                    any questions, feel free to contact us.
                                </p>
                                <p>Meeting link: {appointmentDetails.meetingLink}</p>
                                <p>Looking forward to seeing you soon!</p>
                            </div>
                        </div>
                    }
                    placement="top"
                >
                    <svg width="16" height="16" viewBox="0 0 14 14" fill="none" className={s.questionIcon}>
                        <path
                            d="M4.48511 5C4.85118 4.22321 5.83895 3.66667 7.00004 3.66667C8.4728 3.66667 9.6667 4.5621 9.6667 5.66667C9.6667 6.59963 8.81496 7.38338 7.66285 7.6044C7.30125 7.67377 7.00004 7.96514 7.00004 8.33333M7 10.3333H7.00667M13 7C13 10.3137 10.3137 13 7 13C3.68629 13 1 10.3137 1 7C1 3.68629 3.68629 1 7 1C10.3137 1 13 3.68629 13 7Z"
                            stroke="#6B7280"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </Tooltip>
            </Button>

            <Modal
                title="No meeting link found for this appointment"
                open={showNoMeetingLinkModal}
                onOk={handleGoToAppointmentManagement}
                onCancel={handleCloseModal}
                okText="Go to Appointment Management"
                cancelText="Cancel"
                centered
            >
                <div>
                    <p>Please check the appointment details or set up the meeting link in appointment management.</p>
                </div>
            </Modal>
        </div>
    );
};

export default AppointmentReminderButton;
